// Report-param grammar for the open-ended family, ported from the coffee block
// (importReportParams / updateReportParams / the chart and sort pairs).
//
// Four params carry the block's state, addressed by prefix:
//   oe   how the answers are being looked at - the view mode and that mode's config
//   cm   the chart's mode
//   z    the chart's zoom (shared with the other views of the question)
//   s    the sort order (shared with the other views of the question)
//
// The word-cloud section (wc<id>) is not here: its grammar is shared with every other block that
// draws a cloud and still lives with them.
//
// Nothing in here knows which view modes exist or what they default to - the family's own lib
// answers that, so the combo, conversation and menu-test blocks can pass theirs.

import { isEqual } from 'lodash'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { PERCENT_CHART_MODES } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export { DEFAULT_SORT_ORDER }

// Unlike the items-list base this was ported from, an unrecognised token falls back to the
// default rather than being stored as-is - the sorted index splits on it downstream.
export const resolveSortOrder = (section, defaultSortOrder = DEFAULT_SORT_ORDER) =>
  decodeSortParam(section, { defaultSortOrder })

// A zoom that matches the one the whole report is set to is not the question's own, so the key
// drops out of the persisted set.
export { calcZoomSection } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export const calcSortParamsSection = (sortOrder, defaultSortOrder = DEFAULT_SORT_ORDER) => ({
  s: encodeSortParam(sortOrder, { defaultSortOrder })
})

export const resolveChartMode = (params = {}) => params.cm || null

// The chart-mode space collapses to percents for a question with a base, or one told to show
// percents only; a question counting respondents has no pie to slice.
export const calcDefaultChartMode = ({
  chartMode,
  questionChartMode,
  globalChartMode,
  hasBase,
  percentsOnly,
  isRType
} = {}) => {
  let mode = 'off' === chartMode ? globalChartMode : chartMode
  mode = mode || questionChartMode || globalChartMode

  if (isRType && 'pie' === mode) mode = 'respondents'

  if ((hasBase || true === percentsOnly) && !PERCENT_CHART_MODES.includes(mode)) return 'percents'

  return mode
}

// A chart mode that says what the question already says is not worth storing; the zoom goes with
// it when the mode it was pinned against cannot be zoomed.
export const calcChartParamsSection = ({ chartMode, questionChartMode, format, defaultChartMode } = {}) => {
  const stored = format?.chartMode || questionChartMode || 'off'

  return {
    cm: chartMode === stored ? null : chartMode,
    ...('pie' === defaultChartMode ? { z: null } : {})
  }
}

// A stored token is only worth honouring while it names a mode the family can draw: an
// unrecognised one has no view to mount, and the params carry it to everyone the report is shared
// with. The offered list is consulted alongside the family's own, since a question type can pin a
// mode the family list does not carry.
const isKnownViewMode = (viewMode, { key, type, format, lib }) =>
  lib.viewModesOptions.some(v => v.key === viewMode) ||
  lib.getViewModesOptions(key, type, format || {}).some(v => v.key === viewMode)

// The view mode and its config, as the block opens on them: what the param holds, else what the
// family says this question defaults to. A token that survives none of that takes the default's
// own config with it, and the corrected pair is written back over the stored one.
export const resolveViewModeParams = ({ params = {}, key, type, format, lib } = {}) => {
  const stored = params.oe || {}
  const questionKey = lib.getKey(key, stored.config || {})

  const known = null != stored.viewMode && isKnownViewMode(stored.viewMode, { key: questionKey, type, format, lib })
  const viewMode = known ? stored.viewMode : lib.getDefaultViewMode(questionKey, type, format || {})

  return { viewMode, config: (known && stored.config) || lib.defaultConfig[viewMode] || {} }
}

// The view mode a question opens on is not worth storing, and neither is a config that is just
// that mode's own default -- except the quote view's: the report request asks for quotes only from
// a written config, so a question switched to it from another view writes its default too.
// A mode with no chart to zoom drops the zoom with it.
export const calcViewModeSection = ({ viewMode, config, key, type, format, lib, chartViewModes = [] } = {}) => {
  const defaultConfig = lib.defaultConfig[viewMode] || {}
  const defaultViewMode = lib.getDefaultViewMode(lib.getKey(key, config), type, format || {})
  const isDefaultConfig = !config || isEqual(config, defaultConfig)

  const section = { viewMode }
  if (!isDefaultConfig) section.config = config
  else if ('verbatim' === viewMode && viewMode !== defaultViewMode) section.config = config || defaultConfig

  return {
    oe: isEqual(section, { viewMode: defaultViewMode }) ? null : section,
    ...(chartViewModes.includes(viewMode) ? {} : { z: null })
  }
}

// Whether the composed section says anything the params do not already hold.
export const isViewModeSectionStored = (params = {}, section = {}) => isEqual(params.oe || null, section.oe)
